import { useEffect, useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';
import { useSEO } from '../../utils/seo';

const InvestorsPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goglobalworld.net';
  
  useSEO({
    title: 'Real Estate Investment Opportunities | Reduce Construction Costs 20-40% | Go Global Inc.',
    description: 'Maximize real estate development ROI with wholesale construction materials. Direct import building materials reduce construction costs by 20-40% for property investors and developers. Bulk construction materials supplier for multi-family, commercial, and industrial projects. Steel building packages, turnkey solutions. Equity partnership opportunities for cost-based value creation.',
    keywords: 'real estate investment, construction cost reduction, wholesale construction materials, bulk building materials supplier, property development, direct import building materials, construction equity partnership',
    canonical: `${siteUrl}/investors`,
    ogImage: 'https://public.readdy.ai/ai/img_res/30bf55d3f9935d3cdb3fe3bdb6278523.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Real Estate Investment Opportunities',
      description: 'Reduce construction costs and maximize development ROI through factory-direct wholesale building materials',
      mainEntity: {
        '@type': 'FinancialService',
        name: 'Construction Cost Reduction for Real Estate Investors',
        description: 'Save 20-40% on construction materials through direct import wholesale supply for property developers',
        provider: {
          '@type': 'Organization',
          name: 'Go Global Inc.',
        }
      }
    },
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    investorType: '',
    equityAllocation: '',
    assetType: '',
    alignment: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.alignment !== 'yes') {
      alert('This investment model must align with your strategy to proceed.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d61qhcrn6d2i4h4oad20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          investorType: '',
          equityAllocation: '',
          assetType: '',
          alignment: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20industrial%20steel%20building%20construction%20site%20with%20cranes%20and%20structural%20framework%20against%20clear%20sky%2C%20professional%20architectural%20photography%20with%20clean%20lines%20and%20geometric%20patterns%2C%20minimalist%20composition%20emphasizing%20scale%20and%20precision%2C%20neutral%20tones%20with%20subtle%20warm%20lighting%20creating%20depth%20and%20dimension%2C%20wide%20angle%20perspective%20showcasing%20development%20progress&width=1920&height=1080&seq=inv001&orientation=landscape"
            alt="Industrial Development"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-display text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
          >
            Industrial & Real Estate Development Investment Partners in Edmonton
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl lg:text-2xl text-white/95 mb-10 leading-relaxed max-w-4xl mx-auto"
          >
            Private equity joint ventures for industrial steel buildings and apartments. No loans. No debt. Capital returned through refinance.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a
              href="#partner"
              className="inline-block px-8 py-4 bg-white text-gray-900 text-base font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 whitespace-nowrap cursor-pointer"
            >
              Request Investor Access
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section id="overview" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
            Private Equity Partnerships
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Go Global Inc. partners exclusively with high-net-worth equity investors to develop industrial and multi-family projects in Edmonton and surrounding Alberta markets.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We do not offer or accept loans, debt financing, or private mortgages. Our model is based on true joint-venture equity partnerships. Investors provide capital. Go Global delivers full project execution.
            </p>
          </div>
        </div>
      </section>

      {/* Investment Model Section */}
      <section id="investment-model" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-12 text-center">
            How the Model Works
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Equity Partner Funds Project', desc: 'Capital provided for land acquisition and construction costs' },
              { step: '02', title: 'Go Global Executes', desc: 'Full management of sourcing, logistics, construction, and delivery' },
              { step: '03', title: 'Project Completion', desc: 'Building completed, leased, and stabilized for maximum value' },
              { step: '04', title: 'Property Refinanced', desc: 'Refinanced at new appraised value based on completed asset' },
              { step: '05', title: 'Capital Returned', desc: 'Investor capital returned through refinance proceeds' },
              { step: '06', title: 'Equity Retained & Redeployed', desc: 'Retained equity used for next project. Repeatable. Scalable.' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg border border-gray-200"
              >
                <div className="text-primary-600 font-bold text-sm mb-3">{item.step}</div>
                <h3 className="font-display text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-base text-gray-700 font-medium">
              Repeatable. Scalable. Asset-backed.
            </p>
          </div>
        </div>
      </section>

      {/* Value Creation Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
            Value Creation Through Supply Chain Control
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-10">
            Go Global controls materials, logistics, customs, and construction, allowing us to build below market cost and accelerate timelines.
          </p>
          <div className="bg-gradient-to-br from-primary-50 to-secondary-50 border-l-4 border-primary-600 p-8 rounded-lg">
            <p className="text-lg text-gray-900 font-medium leading-relaxed">
              A simple turn key industrial warehouse can generate up to <strong className="text-primary-700">$1,000,000 in added equity</strong> from day one of completion and stabilization through cost-based value creation, not speculation.
            </p>
          </div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section id="opportunities" className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-12 text-center">
            Current Development Focus
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-building-line',
                title: 'Industrial Warehouses',
                desc: 'High-demand commercial properties with strong lease fundamentals'
              },
              {
                icon: 'ri-home-gear-line',
                title: 'Steel Building Developments',
                desc: 'Cost-efficient structures with rapid construction timelines'
              },
              {
                icon: 'ri-community-line',
                title: 'Multi-Family Apartments',
                desc: 'Residential projects in growing Edmonton submarkets'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg border border-gray-200 text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-primary-100 rounded-full">
                  <i className={`${item.icon} text-3xl text-primary-600`}></i>
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-base text-gray-700">
              Primary focus: <strong>Edmonton and surrounding Alberta markets</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Partner With Us Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
            Who We Partner With
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              'High-net-worth individuals',
              'Family offices',
              'Private equity partners'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-fill text-xl text-primary-600"></i>
                </div>
                <p className="text-base text-gray-700 font-medium">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-lg text-gray-700 leading-relaxed">
            Ideal partners seek hands-off participation, capital recycling through refinance, and long-term asset ownership.
          </p>
        </div>
      </section>

      {/* Investor Intake Form */}
      <section id="partner" className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
            Request Investor Access
          </h2>
          <p className="text-base text-gray-600 text-center mb-12">
            Complete the form below to begin the qualification process
          </p>

          <form onSubmit={handleSubmit} className="bg-white p-8 lg:p-12 rounded-lg border border-gray-200" data-readdy-form id="investor-form">
            <div className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Company / Family Office */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company / Family Office
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Investor Type */}
              <div>
                <label htmlFor="investorType" className="block text-sm font-medium text-gray-700 mb-2">
                  Investor Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="investorType"
                  name="investorType"
                  required
                  value={formData.investorType}
                  onChange={(e) => setFormData({ ...formData, investorType: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select investor type</option>
                  <option value="High-net-worth individual">High-net-worth individual</option>
                  <option value="Family office">Family office</option>
                  <option value="Private equity partner">Private equity partner</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Equity Allocation */}
              <div>
                <label htmlFor="equityAllocation" className="block text-sm font-medium text-gray-700 mb-2">
                  Typical Equity Allocation <span className="text-red-500">*</span>
                </label>
                <select
                  id="equityAllocation"
                  name="equityAllocation"
                  required
                  value={formData.equityAllocation}
                  onChange={(e) => setFormData({ ...formData, equityAllocation: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select allocation range</option>
                  <option value="$500K–$1M">$500K–$1M</option>
                  <option value="$1M–$3M">$1M–$3M</option>
                  <option value="$3M+">$3M+</option>
                </select>
              </div>

              {/* Preferred Asset Type */}
              <div>
                <label htmlFor="assetType" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Asset Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="assetType"
                  name="assetType"
                  required
                  value={formData.assetType}
                  onChange={(e) => setFormData({ ...formData, assetType: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select asset type</option>
                  <option value="Industrial warehouses">Industrial warehouses</option>
                  <option value="Steel building developments">Steel building developments</option>
                  <option value="Multi-family apartments">Multi-family apartments</option>
                  <option value="All of the above">All of the above</option>
                </select>
              </div>

              {/* Alignment Question */}
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Final Qualification <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                  Our model is equity partnerships, not loans, with capital returned through refinance. Does this align with your strategy?
                </p>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="alignment"
                      value="yes"
                      required
                      checked={formData.alignment === 'yes'}
                      onChange={(e) => setFormData({ ...formData, alignment: e.target.value })}
                      className="w-4 h-4 text-primary-600 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Yes, this aligns with my investment strategy</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="alignment"
                      value="no"
                      checked={formData.alignment === 'no'}
                      onChange={(e) => setFormData({ ...formData, alignment: e.target.value })}
                      className="w-4 h-4 text-gray-400 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">No, I am seeking loan opportunities</span>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || formData.alignment !== 'yes'}
                className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white text-base font-semibold rounded-full hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
                <i className="ri-arrow-right-line ml-2"></i>
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  Thank you for your interest. We will review your information and contact you shortly.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  There was an error submitting your request. Please try again or contact us directly.
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Legal Disclosure */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <p className="text-xs text-gray-600 leading-relaxed text-center">
            <strong>Legal Disclosure:</strong> Go Global Inc. does not offer loans or guaranteed returns. Information is for informational purposes only and not a solicitation. All opportunities are subject to due diligence and applicable securities regulations.
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default InvestorsPage;
